import { useState } from "react";
import { useAuth } from "@core/contexts/AuthContext";
import { FEATURES } from "@core/lib/features";
import { logError, ErrorFactory } from "@core/lib/error-handling";
import { PlanId } from "@core/constants/quotas";
import {
  SOLO_CHECKOUT_URL,
  buildSoloCheckoutUrl,
} from "@core/constants/billing";

export interface UseCheckoutOptions {
  returnPath: string; // e.g., "/date/dashboard"
}

/**
 * Ouvre le paiement du plan Solo.
 *
 * #1470 : l'ancienne version cherchait un variant `solo_annual` dans
 * pricing-service, qui ne renvoie que des stubs `pro_*` sans variant —
 * résultat : « Plan non disponible » à chaque clic. Le hook redirige
 * désormais vers le même lien LemonSqueezy que PricingHub
 * (SOLO_CHECKOUT_URL), seule source de vérité du checkout Solo.
 */
export function useCheckout(options: UseCheckoutOptions) {
  const checkoutEnabled = FEATURES.CHECKOUT_ENABLED;
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [needsAuth, setNeedsAuth] = useState(false);

  /**
   * `waiverAcceptedAt` : horodatage ISO de la renonciation au droit de
   * rétractation cochée par l'utilisateur. Transmis à LemonSqueezy comme sur
   * la page Tarifs (buildSoloCheckoutUrl). Les écrans qui affichent la case
   * (modale de quota) bloquent le bouton tant qu'elle n'est pas cochée.
   */
  const handleUpgrade = async (
    plan: PlanId,
    upgradeOptions: { waiverAcceptedAt?: string } = {},
  ) => {
    if (!checkoutEnabled) {
      alert("La fonction de paiement est temporairement désactivée.");
      return;
    }

    // Invité : connexion d'abord, puis retour sur la page d'origine
    if (!user || !user.email) {
      localStorage.setItem("doodates-return-to", options.returnPath);
      setNeedsAuth(true);
      return;
    }

    // Nouveau modèle : Solo uniquement, cycle annual unique
    if (plan !== "SOLO") {
      logError(ErrorFactory.validation("Seul le plan Solo est disponible"), {
        component: "useCheckout",
      });
      return;
    }

    setLoading(true);
    window.location.href = upgradeOptions.waiverAcceptedAt
      ? buildSoloCheckoutUrl(upgradeOptions.waiverAcceptedAt)
      : SOLO_CHECKOUT_URL;
  };

  const dismissAuth = () => setNeedsAuth(false);

  return {
    loading,
    needsAuth,
    handleUpgrade,
    dismissAuth,
    user,
    checkoutEnabled,
  };
}
